// ── 미팅지도 설정: 피드백 옵션 CRUD 패널 ─────────────────────────────────────
// 동적관리 ProductsAndFeedbackSection 의 피드백 블록 이식 (2026-09-01 설정 이관).
import React from 'react';
import { SquarePen, Trash2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { ib } from '../tickettypemanagement/classNames';
import { FEEDBACK_TONES, getFeedbackToneVars } from '../../../components/meetingmap/tones';

export function SettingsFeedbackPanel({
  feedbackOptions, feedbackLoading,
  newFeedbackName, setNewFeedbackName, newFeedbackColorKey, setNewFeedbackColorKey,
  editFbId, setEditFbId, editFbName, setEditFbName, editFbColorKey, setEditFbColorKey,
  confirmFbId, setConfirmFbId,
  addFeedback, saveFeedbackEdit, deleteFeedback, toggleAutoKeep,
  saving,
}) {
  const feedbackPillStyle = (opt, fallback = 'blue') => {
    const tone = getFeedbackToneVars(opt?.color_key, fallback);
    return { background: tone.bg, color: tone.text, borderColor: tone.border };
  };
  const toneSelectClass = 'border border-[var(--border)] rounded-md px-1.5 py-[3px] text-[11px] font-bold text-[var(--text)] bg-[var(--bg-card)] outline-none';

  const renderEditChip = (opt) => (
    <span key={opt.id} className="inline-flex items-center gap-1 bg-white dark:bg-pine-800 border border-accent-500 rounded-lg px-1.5 py-0.5">
      <input autoFocus value={editFbName} onChange={(e) => setEditFbName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveFeedbackEdit(); if (e.key === 'Escape') setEditFbId(null); }} className="w-[88px] border-none outline-none text-xs font-semibold text-[var(--text)] bg-transparent px-0.5 min-w-0" />
      <select value={editFbColorKey} onChange={(e) => setEditFbColorKey(e.target.value)} className={toneSelectClass}>
        {FEEDBACK_TONES.map((tone) => <option key={tone.key} value={tone.key}>{tone.label}</option>)}
      </select>
      <button className={ib('ok')} onClick={saveFeedbackEdit} disabled={saving}>✓</button>
      <button className={ib('cancel')} onClick={() => setEditFbId(null)}>✕</button>
    </span>
  );

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12px] font-extrabold text-[var(--text)]">피드백 옵션</span>
        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[var(--bg-subtle)] text-[var(--text-muted)]">{feedbackOptions.filter((o) => o.is_active).length}개</span>
      </div>
      {feedbackLoading && <div className="text-xs text-[var(--text-muted)]">불러오는 중...</div>}
      {!feedbackLoading && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <div className="text-[11px] font-semibold text-[var(--text-muted)] mb-1.5 tracking-wide">일반 피드백</div>
            <div className="flex flex-wrap gap-1.5 items-center">
              {feedbackOptions.filter((o) => o.is_active && !o.auto_keep).map((opt) => {
                if (editFbId === opt.id) return renderEditChip(opt);
                if (confirmFbId === opt.id) return (
                  <span key={opt.id} className="inline-flex items-center gap-1 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-[7px] py-1 text-[11px]">
                    <span className="text-xs font-semibold text-[var(--text)] max-w-[110px] truncate">삭제?</span>
                    <button className={ib('ok')} onClick={() => deleteFeedback(opt.id)} disabled={saving}>확인</button>
                    <button className={ib('cancel')} onClick={() => setConfirmFbId(null)}>취소</button>
                  </span>
                );
                return (
                  <span key={opt.id} style={feedbackPillStyle(opt)} className="inline-flex items-center gap-1 border rounded-lg px-[7px] py-1 text-xs">
                    <span style={{ width: 7, height: 7, borderRadius: 99, background: getFeedbackToneVars(opt.color_key).solidBg, flexShrink: 0 }} />
                    <span className="text-xs font-semibold max-w-[110px] truncate">{opt.name}</span>
                    {opt.cnt > 0 && <span className="text-[9px] font-bold px-1 rounded-full bg-accent-100 dark:bg-pine-700 text-accent-700 dark:text-accent-300 flex-shrink-0">{opt.cnt}</span>}
                    <button className={cn(ib('edit'), 'text-[11px]')} onClick={() => toggleAutoKeep(opt.id, opt.auto_keep)} disabled={saving} title="자동 킵 적용으로 이동">→</button>
                    <button className={ib('edit')} onClick={() => { setEditFbId(opt.id); setEditFbName(opt.name); setEditFbColorKey(opt.color_key || 'blue'); setConfirmFbId(null); }} title="수정" aria-label="피드백 옵션 수정"><SquarePen size={13} /></button>
                    <button className={ib('del')}  onClick={() => { setConfirmFbId(opt.id); setEditFbId(null); }} title="삭제" aria-label="피드백 옵션 삭제"><Trash2 size={13} /></button>
                  </span>
                );
              })}
              <span className="inline-flex items-center gap-1 bg-[var(--bg-subtle)] border border-dashed border-slate-300 dark:border-slate-600 rounded-lg px-1.5 py-0.5">
                <input value={newFeedbackName} onChange={(e) => setNewFeedbackName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addFeedback(); }} placeholder="새 옵션명" className="w-[84px] border-none outline-none text-xs text-[var(--text-muted)] bg-transparent px-0.5 min-w-0" />
                <select value={newFeedbackColorKey} onChange={(e) => setNewFeedbackColorKey(e.target.value)} className={toneSelectClass}>
                  {FEEDBACK_TONES.map((tone) => <option key={tone.key} value={tone.key}>{tone.label}</option>)}
                </select>
                <button className={ib('add')} onClick={addFeedback} disabled={saving || !newFeedbackName.trim()}>+</button>
              </span>
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-amber-500 mb-1.5 tracking-wide">★ 자동 킵 적용</div>
            <div className="flex flex-wrap gap-1.5 items-center">
              {feedbackOptions.filter((o) => o.is_active && o.auto_keep).length === 0 && <span className="text-xs text-[var(--text-muted)] py-0.5">없음</span>}
              {feedbackOptions.filter((o) => o.is_active && o.auto_keep).map((opt) => {
                if (editFbId === opt.id) return renderEditChip(opt);
                return (
                  <span key={opt.id} style={feedbackPillStyle(opt, 'amber')} className="inline-flex items-center gap-1 border rounded-lg px-[7px] py-1 text-xs">
                    <span style={{ width: 7, height: 7, borderRadius: 99, background: getFeedbackToneVars(opt.color_key, 'amber').solidBg, flexShrink: 0 }} />
                    <span className="text-xs font-semibold max-w-[110px] truncate">{opt.name}</span>
                    {opt.cnt > 0 && <span className="text-[9px] font-bold px-1 rounded-full bg-accent-100 dark:bg-pine-700 text-accent-700 dark:text-accent-300 flex-shrink-0">{opt.cnt}</span>}
                    <button className={cn(ib('edit'), 'text-[11px]')} onClick={() => toggleAutoKeep(opt.id, opt.auto_keep)} disabled={saving} title="일반 피드백으로 이동">←</button>
                    <button className={ib('edit')} onClick={() => { setEditFbId(opt.id); setEditFbName(opt.name); setEditFbColorKey(opt.color_key || 'amber'); setConfirmFbId(null); }} title="이름/색상 수정" aria-label="피드백 옵션 수정"><SquarePen size={13} /></button>
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <div className="text-[11px] text-[var(--text-muted)] leading-relaxed">
        미팅 결과 입력 모달의 피드백 선택지입니다. 자동 킵 적용 옵션을 선택하면 미팅이 자동으로 킵 처리됩니다.
      </div>
    </div>
  );
}
